<script setup lang="ts">
import Dialog from 'primevue/dialog';
import { AppTypography } from '~/components/base';

type CampaignSortOrder = 'latest' | 'oldest';

const visible = defineModel<boolean>('visible', { default: false });
const modelValue = defineModel<CampaignSortOrder>({ default: 'latest' });

const OPTIONS: { value: CampaignSortOrder; label: string }[] = [
  { value: 'latest', label: 'Newest First (default)' },
  { value: 'oldest', label: 'Oldest First' },
];

const select = (value: CampaignSortOrder) => {
  modelValue.value = value;
  visible.value = false;
};
</script>

<template>
  <Dialog
    v-model:visible="visible"
    :draggable="false"
    modal
    header="Sort"
    class="app-modal campaign-sort w-full max-w-md"
    :dismissable-mask="true"
  >
    <div class="flex flex-col gap-4">
      <ul class="flex flex-col">
        <li
          v-for="option in OPTIONS"
          :key="option.value"
          :data-testid="`campaign-sort-${option.value}`"
          class="campaign-sort__row flex cursor-pointer items-center justify-between py-3"
          role="button"
          tabindex="0"
          @click="select(option.value)"
          @keydown.enter="select(option.value)"
          @keydown.space.prevent="select(option.value)"
        >
          <AppTypography type="body">{{ option.label }}</AppTypography>
          <i
            v-if="modelValue === option.value"
            class="pi pi-check campaign-sort__check"
          />
        </li>
      </ul>
    </div>
  </Dialog>
</template>

<!--
  The dialog is teleported to <body> and carries no scoped data-v attribute,
  so scoped :deep() can't reach its chrome (header/content/panel). The
  `.campaign-sort` class *is* on the .p-dialog panel, so the sheet chrome is
  themed in the unscoped block below; MY slot content (rows) keeps its
  scoped styles here.
-->
<style scoped lang="scss">
.campaign-sort {
  &__row {
    // Warm tan divider that reads on the sand sheet (Figma) instead of the
    // cool grey used on white surfaces.
    border-bottom: 1px solid $complementary-color-shade-1;

    &:last-of-type {
      border-bottom: none;
    }
  }

  &__check {
    color: $text-color-brand;
    font-size: 1rem;
  }
}
</style>

<!--
  Unscoped: themes the teleported PrimeVue dialog chrome via the
  `.campaign-sort` class it carries on the .p-dialog panel. Kept narrow to
  this modal so it doesn't leak to other dialogs.
-->
<style lang="scss">
.campaign-sort.p-dialog {
  // Warm sand sheet to match Figma (replaces the default white dialog).
  background-color: #ece3d1;

  .p-dialog-header,
  .p-dialog-content {
    background-color: #ece3d1;
  }

  // Centered "Sort" title, close button pinned to the right independent
  // of the centered title's flex layout.
  .p-dialog-header {
    position: relative;
    justify-content: center;
  }

  .p-dialog-title {
    text-align: center;
  }

  .p-dialog-close-button {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    border: none;
    box-shadow: none;

    // Bare X, no background, coloured to match the other modals' mobile
    // close button ($text-color-subtext, Figma's text/primary on this sand
    // sheet). Alignment follows the base `_dialog.scss` inset
    // ($spacing-sm) — the same offset used by the candidates.index Party
    // filter (CandidatePartyModal.vue), so all mobile filter sheets line up
    // flush with the content below.
    @media (max-width: 639.98px) {
      color: $text-color-subtext;
    }

    // Aligns the button's right edge with the row content's right edge (the
    // header and content regions share the same 24px inset from the panel
    // edge) — same fix as CandidatesModal.vue's close button. `!important`
    // is required: the shared `.app-modal.app-modal.p-dialog .p-dialog-header
    // .p-dialog-close-button` rule in `_dialog.scss` sets `right:
    // $spacing-sm` at 5 class-selectors of specificity, which beats this
    // rule's 3 regardless of source order.
    //
    // PrimeVue's default close button only paints its light-grey circular
    // background on `:hover` — at rest it's transparent. Figma's desktop
    // reference shows that circle persistently, at rest, so it's pinned on
    // unconditionally here, matching the Filter modals' close-button
    // treatment (same tokens, same 28px footprint, same desktop-only scope —
    // the mobile full-screen reference keeps a bare X with no circle).
    @media (min-width: 640px) {
      right: $spacing-xl !important;
      width: 28px;
      height: 28px;
      padding: 0;
      color: $border-color-tag;
      background-color: $surface-color-bg-2;

      &:hover,
      &:enabled:hover {
        background-color: $surface-color-bg-2;
      }
    }
  }
}
</style>
